package com.example.mystybag.login.logic

import com.example.mystybag.common.logic.DialogNavigatorStandard
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.login.model.MerchantRegistrationAddCompanyAddressUIState
import com.example.mystybag.login.model.SignatoryItem
import kotlinx.coroutines.launch

class MerchantRegistrationAddCompanyAddressViewModel(
    context: AppContext,
    private val config: NavigatorMerchantRegistrationStep2
) : ChildViewModel<MerchantRegistrationAddCompanyAddressUIState>(context, MerchantRegistrationAddCompanyAddressUIState()) {

    private val sharedConfig: MerchantRegistrationSharedConfig
        get() = config.sharedConfig

    init {
        val signatories = sharedConfig.signatories.map { signatory ->
            signatory.copy(
                phoneNumber = if (signatory.phoneNumber.isBlank()) sharedConfig.companyPhoneNumber else signatory.phoneNumber,
                dialCode = if (signatory.dialCode.isBlank()) sharedConfig.companyDialCode else signatory.dialCode
            )
        }
        updateState { it.copy(signatories = signatories) }
    }

    override fun onCreate() {
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.registration)
        if (uiState.value.signatories.isEmpty()) {
            navigate(NavigatorAddMol(sharedConfig = sharedConfig))
        }
    }

    override fun onVisibilityChanged(visible: Boolean) {
        super.onVisibilityChanged(visible)
        if (visible) {
            syncSignatoriesFromSharedConfig()
        }
    }

    private fun syncSignatoriesFromSharedConfig() {
        val signatories = sharedConfig.signatories.map { signatory ->
            signatory.copy(
                phoneNumber = if (signatory.phoneNumber.isBlank()) sharedConfig.companyPhoneNumber else signatory.phoneNumber,
                dialCode = if (signatory.dialCode.isBlank()) sharedConfig.companyDialCode else signatory.dialCode
            )
        }
        updateState {
            it.copy(
                signatories = signatories,
                showMolListError = if (signatories.isNotEmpty()) false else it.showMolListError
            )
        }
    }

    fun openAddMol(editingIndex: Int? = null) {
        navigate(NavigatorAddMol(sharedConfig = sharedConfig, editingIndex = editingIndex))
    }

    fun startEditingSignatory(index: Int) {
        openAddMol(editingIndex = index)
    }

    fun requestDeleteSignatory(index: Int) {
        scope.launch {
            showDialog(
                DialogNavigatorStandard(
                    title = sharedString(SR.strings.delete_mol_confirmation_title),
                    message = sharedString(SR.strings.delete_mol_confirmation_message),
                    positiveTitle = sharedString(SR.strings.confirm),
                    negativeTitle = sharedString(SR.strings.cancel),
                    stackedButtons = true
                )
            ).onPositiveClick {
                removeSignatory(index)
            }
        }
    }

    private fun removeSignatory(index: Int) {
        val updatedSignatories = uiState.value.signatories.toMutableList().apply { removeAt(index) }
        sharedConfig.signatories = updatedSignatories
        updateState {
            it.copy(
                signatories = updatedSignatories,
                showMolListError = if (updatedSignatories.isNotEmpty()) false else it.showMolListError
            )
        }
        if (updatedSignatories.isEmpty()) {
            navigate(NavigatorAddMol(sharedConfig = sharedConfig))
        }
    }

    override fun onBackButtonClicked() {
        sharedConfig.signatories = uiState.value.signatories
        val updatedConfig = config.copy(sharedConfig = sharedConfig)
        replaceLast(updatedConfig)
        super.onBackButtonClicked()
    }

    fun onContinue() {
        val signatories = uiState.value.signatories
        val validSignatories = signatories.filter { signatory ->
            signatory.firstName.isNotBlank() &&
                signatory.lastName.isNotBlank() &&
                signatory.phoneNumber.isNotBlank() &&
                signatory.dialCode.isNotBlank()
        }
        if (validSignatories.isEmpty()) {
            updateState { it.copy(showMolListError = true) }
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }
        sharedConfig.signatories = validSignatories
        val updatedConfig = config.copy(sharedConfig = sharedConfig)
        replaceLast(updatedConfig)
        navigate(NavigatorMerchantRegistrationStep3(sharedConfig = sharedConfig))
    }
}
